/**
 * DetailTabs Component
 *
 * Standardized tabs for detail pages with:
 * - Icon support
 * - Badge support for counts
 * - Consistent styling
 * - Keyboard navigation
 */

import { ReactNode } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'

export interface DetailTabsProps {
  tabs: Array<{
    value: string
    label: string
    icon?: ReactNode
    content: ReactNode
    badge?: number | string
    disabled?: boolean
  }>
  defaultTab?: string
  className?: string
}

export function DetailTabs({ tabs, defaultTab, className }: DetailTabsProps) {
  const defaultValue = defaultTab || tabs[0]?.value

  return (
    <Tabs defaultValue={defaultValue} className={className}>
      <TabsList>
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled}
            className="gap-2"
          >
            {tab.icon && <span className="shrink-0">{tab.icon}</span>}
            <span>{tab.label}</span>
            {tab.badge !== undefined && (
              <Badge variant="secondary" className="ml-1">
                {tab.badge}
              </Badge>
            )}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value} className="mt-6">
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  )
}
